import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Checkbox, Input, Modal, Select } from '@/shared/components';
import { extractAllowedOptions, minorUnitsFromAmount, normalizeAmountString } from '@/shared/components/form/config-value';
import { getFieldErrors } from '@/services/api';
import { apiErrorMessage } from '@/shared/utils/apiErrorMessage';
import { fetchConfigOptions, isCampaignOnlyConfigGroup } from '@/features/smr-admin/services/smr-admin';
import type { ConfigOptionRecord } from '@/features/smr-admin/services/smr-admin';
import type { MoneyJson } from '@/features/products/types/products.types';
import type { useConfigSettingMutations } from '../hooks/useConfigSettingMutations';

type SettingValue = string | number | boolean | string[] | MoneyJson;

// Product-side groups only. `policies`/`general`/`money_handling` are campaign-only
// groups and are intentionally excluded here so they cannot be applied as product
// config settings (see `isCampaignOnlyConfigGroup`). The "Any group" path is
// additionally filtered client-side below to keep campaign-only rows out.
// `payment_method` is PRODUCT-only (D5/D10) and belongs here.
type GroupKey = 'financial' | 'coverage' | 'payment_method';
const GROUP_OPTIONS: { value: GroupKey | ''; label: string }[] = [
    { value: '', label: 'Any group' },
    { value: 'financial', label: 'Financial' },
    { value: 'coverage', label: 'Coverage' },
    { value: 'payment_method', label: 'Payment Method' },
];

const BOOLEAN_DEFAULT_OPTIONS = [
    { value: '', label: '— select —' },
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' },
];

const TWO_PLACE_AMOUNT = /^\d+(\.\d{1,2})?$/;
const DEFAULT_CURRENCY = 'GBP';

function buildMoneyValue(raw: string, option: ConfigOptionRecord): MoneyJson | null {
    const trimmed = raw.trim();
    if (!TWO_PLACE_AMOUNT.test(trimmed)) return null;
    const amount = normalizeAmountString(trimmed);
    const minor = amount === null ? null : minorUnitsFromAmount(amount);
    if (amount === null || minor === null) return null;
    const pinned = option.validation_rules?.currency;
    return { amount, currency: typeof pinned === 'string' ? pinned : DEFAULT_CURRENCY, minor };
}

function hasUnmappedError(errors: Record<string, string[]>, listed: ConfigOptionRecord[]): boolean {
    const mappedKeys = new Set(listed.flatMap((o) => [o.key_name, `defaults.${o.key_name}.value`]));
    const fields = Object.keys(errors);
    return fields.length === 0 || fields.some((field) => !mappedKeys.has(field));
}

interface Props {
    productId: string;
    existingKeys: string[];
    mutations: ReturnType<typeof useConfigSettingMutations>;
    onClose: () => void;
    isOptionAvailable?: (option: ConfigOptionRecord) => boolean;
}

export function ApplyCatalogModal({ existingKeys, mutations, onClose, isOptionAvailable }: Props) {
    const [group, setGroup] = React.useState<GroupKey | ''>('');
    const [values, setValues] = React.useState<Record<string, SettingValue>>({});
    const [errors, setErrors] = React.useState<Record<string, string[]>>({});
    const [formError, setFormError] = React.useState<string | null>(null);

    const { data: optionsPage } = useQuery({
        queryKey: ['smr-admin', 'config-options', 'group', group],
        queryFn: () => fetchConfigOptions({ group: group || undefined, per_page: 100 }),
        enabled: true,
    });

    // Exclude campaign-only groups from the product picker. This guards the "Any group"
    // path (group === '' -> undefined -> backend returns every group, including the
    // campaign-only ones seeded into the shared catalogue).
    const options: ConfigOptionRecord[] = (optionsPage?.data ?? []).filter(
        (o) => !isCampaignOnlyConfigGroup(o.group),
    );
    const newOptions = options.filter(
        (o) => !existingKeys.includes(o.key_name) && (isOptionAvailable?.(o) ?? true),
    );

    const getFieldError = (key: string): string | undefined =>
        errors[`defaults.${key}.value`]?.[0] ?? errors[key]?.[0];

    const submit = () => {
        const defaults: Record<string, SettingValue> = {};
        const invalid: Record<string, string[]> = {};
        for (const o of newOptions) {
            const v = values[o.key_name];
            if (v === undefined || v === '') continue;
            if (o.data_type === 'money') {
                const money = buildMoneyValue(String(v), o);
                if (money === null) {
                    invalid[`defaults.${o.key_name}.value`] = ['Enter an amount with at most two decimal places'];
                } else {
                    defaults[o.key_name] = money;
                }
                continue;
            }
            // An empty multiselect selection must not submit an empty array.
            if (Array.isArray(v) && v.length === 0) continue;
            defaults[o.key_name] = v;
        }
        setErrors(invalid);
        setFormError(null);
        if (Object.keys(invalid).length > 0) return;
        if (Object.keys(defaults).length === 0) return;
        mutations.applyCatalogMutation.mutate(
            { group: group || undefined, defaults },
            {
                onSuccess: () => onClose(),
                onError: (err: unknown) => {
                    const fieldErrors = getFieldErrors(err);
                    setErrors(fieldErrors);
                    setFormError(
                        hasUnmappedError(fieldErrors, newOptions)
                            ? apiErrorMessage(err, 'Failed to apply catalog group')
                            : null,
                    );
                },
            },
        );
    };

    const isPending = mutations.applyCatalogMutation.isPending;

    return (
        <Modal
            isOpen
            onClose={onClose}
            ariaLabelledBy="apply-catalog-heading"
            className="max-w-lg p-6"
        >
            <div className="space-y-4">
                <h2 id="apply-catalog-heading" className="text-lg font-semibold">Apply catalog group</h2>

                <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="apply-catalog-group">Group</label>
                    <Select
                        id="apply-catalog-group"
                        placeholder="Group"
                        value={group}
                        onChange={(v) => {
                            setGroup(v as GroupKey | '');
                            setValues({});
                            setErrors({});
                            setFormError(null);
                        }}
                        options={GROUP_OPTIONS}
                    />
                </div>

                {formError && <Alert variant="error">{formError}</Alert>}

                {newOptions.length === 0 && (
                    <p className="text-sm text-mp-muted">
                        {options.length === 0
                            ? 'No catalog options found for this group.'
                            : 'All catalog options in this group are already applied to this product.'}
                    </p>
                )}

                {newOptions.length > 0 && (
                    <div className="space-y-3 max-h-80 overflow-y-auto">
                        {newOptions.map((opt) => (
                            <div key={opt.key_name} className="space-y-1">
                                <label className="text-sm font-medium" htmlFor={`apply-${opt.key_name}`}>
                                    {opt.label ?? opt.key_name}
                                    {opt.is_required && <span className="text-mp-error ml-1">*</span>}
                                </label>
                                {(opt.data_type === 'integer' || opt.data_type === 'decimal') && (
                                    <Input
                                        id={`apply-${opt.key_name}`}
                                        type="number"
                                        step={opt.data_type === 'decimal' ? 0.01 : 1}
                                        value={values[opt.key_name] !== undefined ? String(values[opt.key_name]) : ''}
                                        onChange={(e) => setValues((v) => ({ ...v, [opt.key_name]: e.target.value === '' ? '' : Number(e.target.value) }))}
                                        placeholder={opt.key_name}
                                    />
                                )}
                                {opt.data_type === 'string' && (
                                    <Input
                                        id={`apply-${opt.key_name}`}
                                        value={String(values[opt.key_name] ?? '')}
                                        onChange={(e) => setValues((v) => ({ ...v, [opt.key_name]: e.target.value }))}
                                        placeholder={opt.key_name}
                                    />
                                )}
                                {opt.data_type === 'boolean' && (
                                    <Select
                                        id={`apply-${opt.key_name}`}
                                        size="sm"
                                        ariaLabel={opt.label ?? opt.key_name}
                                        placeholder="— select —"
                                        options={BOOLEAN_DEFAULT_OPTIONS}
                                        value={values[opt.key_name] === undefined ? undefined : String(values[opt.key_name])}
                                        onChange={(value) =>
                                            setValues((v) => {
                                                // Re-selecting the placeholder clears the value; coercing it to
                                                // false would pass the `!== ''` submit filter.
                                                if (value === '') {
                                                    const next = { ...v };
                                                    delete next[opt.key_name];
                                                    return next;
                                                }
                                                return { ...v, [opt.key_name]: value === 'true' };
                                            })
                                        }
                                    />
                                )}
                                {opt.data_type === 'money' && (
                                    <Input
                                        id={`apply-${opt.key_name}`}
                                        type="number"
                                        step={0.01}
                                        value={values[opt.key_name] !== undefined ? String(values[opt.key_name]) : ''}
                                        onChange={(e) => setValues((v) => ({ ...v, [opt.key_name]: e.target.value }))}
                                        placeholder="Amount"
                                    />
                                )}
                                {opt.data_type === 'multiselect' && (() => {
                                    const allowed = extractAllowedOptions(opt);
                                    if (allowed.length === 0) {
                                        return (
                                            <p className="text-mp-error text-xs">
                                                This option defines no selectable values.
                                            </p>
                                        );
                                    }
                                    const current = Array.isArray(values[opt.key_name])
                                        ? (values[opt.key_name] as string[])
                                        : [];
                                    return (
                                        <div
                                            role="group"
                                            aria-label={opt.label ?? opt.key_name}
                                            className="flex flex-col gap-2"
                                        >
                                            {allowed.map((av) => (
                                                <Checkbox
                                                    key={av.value}
                                                    id={`apply-${opt.key_name}-${av.value}`}
                                                    label={av.label}
                                                    ariaLabel={av.label}
                                                    checked={current.includes(av.value)}
                                                    onChange={(checked) =>
                                                        setValues((v) => {
                                                            const cur = Array.isArray(v[opt.key_name])
                                                                ? (v[opt.key_name] as string[])
                                                                : [];
                                                            const next = checked
                                                                ? [...cur, av.value]
                                                                : cur.filter((x) => x !== av.value);
                                                            // Serialise in declared order for stable payloads.
                                                            return { ...v, [opt.key_name]: allowed.filter((a) => next.includes(a.value)).map((a) => a.value) };
                                                        })
                                                    }
                                                />
                                            ))}
                                        </div>
                                    );
                                })()}
                                {getFieldError(opt.key_name) && (
                                    <p className="text-mp-error text-xs">{getFieldError(opt.key_name)}</p>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={isPending}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        disabled={isPending || newOptions.length === 0}
                        onClick={submit}
                    >
                        {isPending ? 'Applying…' : 'Apply'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
